import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';

/** 10px 菱形与 8px 圆点的填充面积接近，仅用于趋势图 symbol。 */
export const TRUE_DATA_CHART_DIAMOND_SIZE = 10;

/** 仅改写已声明数据源展示元数据的图例；其余 series 保留 useLineCharts 原配置。 */
export const applySeriesLegendDisplay = (
  lineOption: Record<string, any>,
  lineSourceIcon?: any,
): Record<string, any> => {
  const legend = lineOption?.legend;
  const sourceDisplaySeriesList = Array.isArray(lineOption?.series)
    ? lineOption.series.filter(
      (series: any) => series?.dataSourceKey && series?.dbTooltipName,
    )
    : [];
  if (!legend || sourceDisplaySeriesList.length === 0) return lineOption;

  let pageSourceIconUrl = null;
  if (lineSourceIcon) {
    pageSourceIconUrl =
      typeof lineSourceIcon === 'string'
        ? getDataSourceIcon(lineSourceIcon)
        : lineSourceIcon;
  }
  const legendMetaList = sourceDisplaySeriesList
    .filter((series: any) => series?.name)
    .map((series: any, index: number) => {
      const iconUrl =
        pageSourceIconUrl ||
        (series?.dataSourceKey
          ? getDataSourceIcon(series.dataSourceKey)
          : null);
      return {
        name: series.name,
        displayName:
          series?.dbTooltipName || series.name,
        isTrueData: Boolean(series?.dbIsTrueData),
        color: series?.itemStyle?.color,
        iconUrl,
        iconRichKey: iconUrl ? `seriesSourceIcon${index}` : '',
      };
    });
  const legendMetaByName = new Map(
    legendMetaList.map((item: any) => [item.name, item]),
  );
  const sourceIconRichStyles = Object.fromEntries(
    legendMetaList
      .filter((item: any) => item.iconRichKey && item.iconUrl)
      .map((item: any) => [
        item.iconRichKey,
        {
          height: 15,
          width: 15,
          backgroundColor: { image: item.iconUrl },
        },
      ]),
  );

  return {
    ...lineOption,
    legend: {
      ...legend,
      data: Array.isArray(legend?.data)
        ? legend.data.map((item: any) => {
          const name = typeof item === 'string' ? item : item?.name;
          const meta = legendMetaByName.get(name) as any;
          if (!meta?.isTrueData) return item;
          return {
            ...(typeof item === 'object' ? item : {}),
            name,
            icon: 'diamond',
            itemStyle: {
              ...(typeof item === 'object' ? item?.itemStyle : {}),
              color: meta.color,
              borderColor: meta.color,
              borderWidth: 0,
            },
          };
        })
        : legend?.data,
      ...(legendMetaList.some((item: any) => item.isTrueData)
        ? {
          itemWidth: TRUE_DATA_CHART_DIAMOND_SIZE,
          itemHeight: TRUE_DATA_CHART_DIAMOND_SIZE,
        }
        : {}),
      textStyle: {
        ...(legend?.textStyle || {}),
        rich: {
          ...(legend?.textStyle?.rich || {}),
          ...sourceIconRichStyles,
        },
      },
      formatter: (name: string) => {
        const meta = legendMetaByName.get(name) as any;
        if (meta) {
          const iconText = meta.iconRichKey
            ? `{${meta.iconRichKey}|}`
            : '';
          return `${iconText} {a| ${meta.displayName}}`;
        }
        if (typeof legend?.formatter === 'function') {
          return legend.formatter(name);
        }
        return name;
      },
    },
  };
};

/**
 * 移除 tooltip 指标名前已经由数据源 icon 表达的前缀。
 * qualifiers 按顺序处理，支持英文空格分隔和中文直接拼接两种文案。
 */
export const stripLeadingMetricQualifiers = (
  text = '',
  qualifiers: string[] = [],
): string => {
  let displayText = text.trim();
  const normalizedQualifiers = Array.from(
    new Set(qualifiers.map((item) => item?.trim()).filter(Boolean)),
  );

  normalizedQualifiers.forEach((qualifier) => {
    if (
      displayText.toLocaleLowerCase().startsWith(qualifier.toLocaleLowerCase())
    ) {
      displayText = displayText.slice(qualifier.length).trim();
    }
  });

  return displayText || text.trim();
};

/** 真数系列统一使用“指标名 - True data”结构，避免各入口自行处理连字符。 */
export const formatTrueDataSeriesName = (
  metricName = '',
  trueDataSuffix = '',
): string => {
  const normalizedMetricName = metricName.trim().replace(/-+\s*$/, '');
  const normalizedSuffix = trueDataSuffix.trim().replace(/^-+\s*/, '');
  if (!normalizedMetricName) return normalizedSuffix;
  if (!normalizedSuffix) return normalizedMetricName;
  return `${normalizedMetricName} - ${normalizedSuffix}`;
};
